/**
 * AllocatedRatesVfm virtual views: five concern-scoped, ancestry-enriched
 * surfaces fronting the ~33-column `AllocatedRates_VFM_String_Agent` source
 * (VFM (virtual flow metering) allocated rates, string-grain,
 * StringContext).
 *
 * The source carries the WF05 VFM allocation solve as a single wide row keyed by
 * the `wellString` producing-string relation: the preferred-method average rates
 * and volumes, the weighted-average-method rates and volume, the per-method
 * average oil-rate estimates, the final allocated phase rates from both methods,
 * and the un-prefixed injection / uptime / status context. Each view here slices
 * that wide row into one concern, de-prefixes to canonical names via the shared
 * shapes, and enriches each row with its well / field / reservoir ancestry
 * resolved through the FlatHierarchy registry.
 *
 * Unlike the sibling EstimatedRates views (whose measures de-prefix on a uniform
 * analysis-METHOD axis), this source has NO single uniform de-prefix axis. Only
 * the preferred-method family is a clean prefix axis (`prefix:'preferredVFM'`);
 * the others mix prefixes (`weightedAverageVFM` vs bare `weightedAverage`), carry
 * irregular method infixes (`SystemAnalysis` vs bare `IPR`/`ESP`/…), or split the
 * allocated-rate prefix (`vfmWeightedAverageMethod*` oil vs `vfmWeightedAverage*`
 * gas/water). Those families are authored as bespoke verbatim-suffix shapes in
 * `shapes.ts` (VFM_*) applied with `prefix:''` to keep
 * `prefix + suffix === source column`. The `PHASE_RATES`/`PHASE_RATES_STD`
 * shapes do NOT fit any VFM rate cluster (every rate is an `Average…Rate` with
 * an associated/non-associated gas split) and are deliberately not reused.
 *
 * Built per session (not module-level) because the enriched build closes over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import {
  VFM_ALLOCATED_RATES,
  VFM_ALLOCATION_CONTEXT,
  VFM_METHOD_OIL_RATES,
  VFM_PREFERRED_RATES,
  VFM_WEIGHTED_RATES,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { AllocatedRatesVfmStringAgentView } from '#schema/generated/ipso/views.ts';

/** StringContext: well/field/reservoir ancestry off the `wellString` ref. */

/** One concern-scoped VFM allocation surface: a canonical name, agent doc, and the de-prefixed groups to keep. */
type AllocatedRatesVfmSpec = ViewFamilySpec;

/**
 * The five AllocatedRatesVfm subviews, declared as data. Each de-prefixes one
 * concern of the VFM allocation measure family; all share identity + the
 * wellString relation and the well/field/reservoir ancestry enrichment.
 *
 * Only `VfmPreferredAllocation` uses a clean prefix axis
 * (`prefix:'preferredVFM'`); the weighted, per-method, and allocated-rate
 * families mix or split their source prefixes, so they are authored as bespoke
 * verbatim shapes applied with the default empty prefix.
 */
const ALLOCATED_RATES_VFM_SPECS: readonly AllocatedRatesVfmSpec[] = [
  {
    name: 'VfmPreferredAllocation',
    description: `Per-string preferred-VFM-method average production rates and estimated volumes (oil / associated-gas / non-associated-gas / water average rate, estimated well oil & water volume) plus the selected-method data-source flag${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: VFM_PREFERRED_RATES, prefix: 'preferredVFM' }],
  },
  {
    name: 'VfmWeightedAllocation',
    description: `Per-string weighted-average-VFM-method average production rates and estimated oil volume (oil / associated-gas / non-associated-gas / water average rate, estimated well oil volume) plus the weighted-method data-source flag${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: VFM_WEIGHTED_RATES }],
  },
  {
    name: 'VfmMethodEstimates',
    description: `Per-string per-method average oil-rate estimates from each VFM analysis (system, IPR, VLP, choke, ESP, data-driven), for comparing how each method values the same string${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: VFM_METHOD_OIL_RATES }],
  },
  {
    name: 'VfmAllocatedRates',
    description: `Per-string final allocated phase rates from both the preferred and the weighted-average VFM methods (oil / associated-gas / non-associated-gas / water allocation rate per method), for comparing allocated rates across methods${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: VFM_ALLOCATED_RATES }],
  },
  {
    name: 'VfmAllocationContext',
    description: `Per-string shared VFM allocation context: average water and gas injection rates, the oil allocation workflow status, and well uptime over the allocation period${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: VFM_ALLOCATION_CONTEXT }],
  },
];

/**
 * Build the five de-prefixed, ancestry-enriched AllocatedRatesVfm surfaces, each
 * a StringContext view over the shared source + well/field/reservoir enrichment;
 * filter by wellString (or join WellEntity) to scope by hierarchy.
 */
export const useAllocatedRatesVfmViews = makeStringContextViews({
  source: () => AllocatedRatesVfmStringAgentView,
  specs: ALLOCATED_RATES_VFM_SPECS,
});
